import type { AssetId } from '../../shared/types/ids';

/**
 * Pictures the *platform* needs, named by the job they do (Slice E″, PS5-4).
 *
 * The shell draws two things every camera game shares and no game owns: the invitation back into
 * frame when the camera has lost somebody, and hero training. They were words on a panel — correct,
 * kind, and unmistakably a developer's dialog box, which is exactly the "no longer resembles a
 * developer diagnostic tool" criterion PLAN P5 exits on.
 *
 * The seam is `PLATFORM_AUDIO_ROLES`' (D-094(3)/PS5-2), deliberately: the platform declares what a
 * role *is*, a theme opts in by declaring an asset under that ID, and the resolver does the rest. No
 * platform code imports a game, no scene holds a filename, and a theme that declares none of these
 * simply gets the panel it has today — a missing picture falls back and then falls away, it never
 * fails (D-014).
 *
 * **Nothing here distinguishes one child from another.** The recovery invitation is addressed to a
 * lane by name in words already; the picture is the same one for every hero, because a picture that
 * marked out the child the camera had lost would be the comparison the non-competitive invariant
 * forbids.
 */
export const PLATFORM_ASSET_ROLES = {
  /** Hero training: the outline a child stands inside (brief §9). */
  calibrationBodyGuide: 'ar.calibration.body-guide.single.v1',
  /** Hero training, finished. */
  calibrationSuccess: 'ar.calibration.success.v1',
  /** "Come back where we can see you" (GDS §36). */
  trackingReturn: 'ar.tracking.return-to-position.v1',
  /** Somebody has stepped out of the picture altogether. */
  trackingLeftFrame: 'ar.tracking.player-left-frame.v1',
  /** The camera can see, but not clearly. */
  trackingUnclear: 'ar.tracking.low-confidence.v1',
} as const satisfies Record<string, AssetId>;
